/**
 * PBF (Position-Based Fluids) Solver WGSL Compute Shaders
 *
 * PBF enforces incompressibility via position corrections:
 * 1. predictPositions: x* = x + dt * (v + dt * g)
 * 2. computeLambda: Compute constraint scaling factors
 * 3. computeDeltaPos: Compute position corrections with tensile stability
 * 4. applyDeltaPos: Apply corrections (repeat 2-4 for pbfIter iterations)
 * 5. updateVelocityXSPH: Derive velocity from position change + XSPH smoothing
 *
 * Reference: Macklin & Müller 2013 "Position Based Fluids"
 */

import { SPH_STRUCT_TANIMLARI, SPH_KERNEL_FONKSIYONLARI, SPH_GRID_YARDIMCI, SPH_ORTAK_BINDING_TANIMLARI } from './sph-ortak-shaders.wgsl';
import { GRID_NEIGHBOR_HELPER } from './sph-grid-shaders.wgsl';

// ============================================================================
// PBF SOLVER-SPECIFIC BINDINGS (@group(3))
// ============================================================================

export const PBF_SOLVER_BINDINGS = /* wgsl */`
// PBF Solver Buffers
@group(3) @binding(0) var<storage, read_write> lambda: array<f32>;
@group(3) @binding(1) var<storage, read_write> deltaPos: array<vec4<f32>>;
@group(3) @binding(2) var<storage, read_write> predictedPos: array<vec4<f32>>;
`;

// ============================================================================
// PASS 1: PREDICT POSITIONS
// ============================================================================

const PBF_PREDICT_POSITIONS_SHADER = /* wgsl */`
${SPH_STRUCT_TANIMLARI}
${SPH_KERNEL_FONKSIYONLARI}
${SPH_GRID_YARDIMCI}
${SPH_ORTAK_BINDING_TANIMLARI}
${PBF_SOLVER_BINDINGS}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  if (i >= params.activeCount) { return; }

  let pos = positionMass[i].xyz;
  let vel = velocityAge[i].xyz;
  let gravity = vec3<f32>(params.gravity_x, params.gravity_y, params.gravity_z);

  // Predict position with gravity
  let predicted = pos + params.dt * (vel + params.dt * gravity);
  predictedPos[i] = vec4<f32>(predicted, positionMass[i].w);
}
`;

export const PBF_PREDICT_POSITIONS = PBF_PREDICT_POSITIONS_SHADER;

// ============================================================================
// PASS 2: COMPUTE LAMBDA (Constraint Scaling Factors)
// ============================================================================

const PBF_COMPUTE_LAMBDA_SHADER = /* wgsl */`
${SPH_STRUCT_TANIMLARI}
${SPH_KERNEL_FONKSIYONLARI}
${SPH_GRID_YARDIMCI}
${SPH_ORTAK_BINDING_TANIMLARI}
${PBF_SOLVER_BINDINGS}
${GRID_NEIGHBOR_HELPER}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  if (i >= params.activeCount) { return; }

  let h = params.smoothingRadius;
  let h2 = h * h;
  let poly6C = 315.0 / (64.0 * 3.14159265359 * pow(h, 9.0));
  let spikyC = -45.0 / (3.14159265359 * pow(h, 6.0));
  let rho0 = params.restDensity;

  let gridOrigin = vec3<f32>(params.gridOrigin_x, params.gridOrigin_y, params.gridOrigin_z);
  let gridDim = vec3<u32>(params.gridDim_x, params.gridDim_y, params.gridDim_z);
  let pos_i = predictedPos[i].xyz;
  let myCell = posToCell(pos_i, gridOrigin, params.gridCellSize);

  // Compute density at predicted position
  var rho_i: f32 = 0.0;
  var sum_grad_sq: f32 = 0.0;
  var sum_grad: vec3<f32> = vec3<f32>(0.0);

  // Self-contribution
  let mass_i = 1.0 / positionMass[i].w;
  rho_i += mass_i * kernelPoly6(0.0, h, poly6C);

  // Neighbor contributions
  for (var dz: i32 = -1; dz <= 1; dz++) {
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nCell = myCell + vec3<i32>(dx, dy, dz);
        if (!cellInBounds(nCell, gridDim)) { continue; }
        let cellIdx = cellToIndex(nCell, gridDim);
        let start = prefixSum[cellIdx];
        let end = select(prefixSum[cellIdx + 1u], params.activeCount, cellIdx + 1u >= params.totalGridCells);

        for (var k = start; k < end; k++) {
          let j = sortedIndex[k];
          if (j == i) { continue; }

          let r = pos_i - predictedPos[j].xyz;
          let r2 = dot(r, r);

          if (r2 < h2) {
            let dist = sqrt(r2);
            let mass_j = 1.0 / positionMass[j].w;

            // Density contribution
            rho_i += mass_j * kernelPoly6(r2, h, poly6C);

            // Gradient contribution: grad_pj C_i = (1/rho0) * kernelSpikyGrad
            let grad = (1.0 / rho0) * kernelSpikyGrad(r, dist, h, spikyC);
            sum_grad += grad;
            sum_grad_sq += dot(grad, grad);
          }
        }
      }
    }
  }

  // Self-gradient contribution (grad_pi C_i = -sum_j grad_pj C_i)
  sum_grad_sq += dot(sum_grad, sum_grad);

  // Store density
  densityMisc[i].x = rho_i;

  // Compute lambda: lambda_i = -C_i / (sum||grad_C||^2 + epsilon)
  let C_i = rho_i / rho0 - 1.0;
  let denom = sum_grad_sq + params.pbf_epsilon;
  lambda[i] = select(-C_i / denom, 0.0, abs(C_i) < 1e-6);
}
`;

export const PBF_COMPUTE_LAMBDA = PBF_COMPUTE_LAMBDA_SHADER;

// ============================================================================
// PASS 3: COMPUTE DELTA POSITION (Position Corrections)
// ============================================================================

const PBF_COMPUTE_DELTA_POS_SHADER = /* wgsl */`
${SPH_STRUCT_TANIMLARI}
${SPH_KERNEL_FONKSIYONLARI}
${SPH_GRID_YARDIMCI}
${SPH_ORTAK_BINDING_TANIMLARI}
${PBF_SOLVER_BINDINGS}
${GRID_NEIGHBOR_HELPER}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  if (i >= params.activeCount) { return; }

  let h = params.smoothingRadius;
  let h2 = h * h;
  let poly6C = 315.0 / (64.0 * 3.14159265359 * pow(h, 9.0));
  let spikyC = -45.0 / (3.14159265359 * pow(h, 6.0));
  let rho0 = params.restDensity;

  let gridOrigin = vec3<f32>(params.gridOrigin_x, params.gridOrigin_y, params.gridOrigin_z);
  let gridDim = vec3<u32>(params.gridDim_x, params.gridDim_y, params.gridDim_z);
  let pos_i = predictedPos[i].xyz;
  let myCell = posToCell(pos_i, gridOrigin, params.gridCellSize);

  // Tensile instability correction parameters
  let deltaQ = 0.1 * h;
  let deltaQ2 = deltaQ * deltaQ;
  let wDeltaQ = kernelPoly6(deltaQ2, h, poly6C);

  var dp = vec3<f32>(0.0);

  // Neighbor loop
  for (var dz: i32 = -1; dz <= 1; dz++) {
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nCell = myCell + vec3<i32>(dx, dy, dz);
        if (!cellInBounds(nCell, gridDim)) { continue; }
        let cellIdx = cellToIndex(nCell, gridDim);
        let start = prefixSum[cellIdx];
        let end = select(prefixSum[cellIdx + 1u], params.activeCount, cellIdx + 1u >= params.totalGridCells);

        for (var k = start; k < end; k++) {
          let j = sortedIndex[k];
          if (j == i) { continue; }

          let r = pos_i - predictedPos[j].xyz;
          let r2 = dot(r, r);

          if (r2 < h2) {
            let dist = sqrt(r2);

            // Tensile instability correction: s_corr = -k * (W(r) / W(deltaQ))^n
            let wR = kernelPoly6(r2, h, poly6C);
            let scorr = -params.pbf_scorr_k * pow(wR / max(wDeltaQ, 1e-9), params.pbf_scorr_n);

            // Position correction: deltaP += (lambda_i + lambda_j + s_corr) * grad_W
            let grad = kernelSpikyGrad(r, dist, h, spikyC);
            dp += (lambda[i] + lambda[j] + scorr) * grad;
          }
        }
      }
    }
  }

  // Scale by 1/rho0
  deltaPos[i] = vec4<f32>(dp / rho0, 0.0);
}
`;

export const PBF_COMPUTE_DELTA_POS = PBF_COMPUTE_DELTA_POS_SHADER;

// ============================================================================
// PASS 4: APPLY DELTA POSITION
// ============================================================================

const PBF_APPLY_DELTA_POS_SHADER = /* wgsl */`
${SPH_STRUCT_TANIMLARI}
${SPH_KERNEL_FONKSIYONLARI}
${SPH_GRID_YARDIMCI}
${SPH_ORTAK_BINDING_TANIMLARI}
${PBF_SOLVER_BINDINGS}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  if (i >= params.activeCount) { return; }

  // Apply position correction
  let correctedPos = predictedPos[i].xyz + deltaPos[i].xyz;
  predictedPos[i] = vec4<f32>(correctedPos, predictedPos[i].w);
}
`;

export const PBF_APPLY_DELTA_POS = PBF_APPLY_DELTA_POS_SHADER;

// ============================================================================
// PASS 5: UPDATE VELOCITY + XSPH SMOOTHING
// ============================================================================

const PBF_UPDATE_VELOCITY_XSPH_SHADER = /* wgsl */`
${SPH_STRUCT_TANIMLARI}
${SPH_KERNEL_FONKSIYONLARI}
${SPH_GRID_YARDIMCI}
${SPH_ORTAK_BINDING_TANIMLARI}
${PBF_SOLVER_BINDINGS}
${GRID_NEIGHBOR_HELPER}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  if (i >= params.activeCount) { return; }

  let h = params.smoothingRadius;
  let h2 = h * h;
  let poly6C = 315.0 / (64.0 * 3.14159265359 * pow(h, 9.0));

  let gridOrigin = vec3<f32>(params.gridOrigin_x, params.gridOrigin_y, params.gridOrigin_z);
  let gridDim = vec3<u32>(params.gridDim_x, params.gridDim_y, params.gridDim_z);

  let newPos = predictedPos[i].xyz;
  let oldPos = positionMass[i].xyz;

  // Derive velocity from position change
  var newVel = (newPos - oldPos) / params.dt;

  // XSPH viscosity smoothing
  let myCell = posToCell(newPos, gridOrigin, params.gridCellSize);
  var xsphVel = vec3<f32>(0.0);

  for (var dz: i32 = -1; dz <= 1; dz++) {
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nCell = myCell + vec3<i32>(dx, dy, dz);
        if (!cellInBounds(nCell, gridDim)) { continue; }
        let cellIdx = cellToIndex(nCell, gridDim);
        let start = prefixSum[cellIdx];
        let end = select(prefixSum[cellIdx + 1u], params.activeCount, cellIdx + 1u >= params.totalGridCells);

        for (var k = start; k < end; k++) {
          let j = sortedIndex[k];
          if (j == i) { continue; }

          let r = newPos - predictedPos[j].xyz;
          let r2 = dot(r, r);

          if (r2 < h2) {
            let rho_j = densityMisc[j].x;
            let v_j = (predictedPos[j].xyz - positionMass[j].xyz) / params.dt;

            // XSPH: v_xsph += (1/rho_j) * (v_j - v_i) * W(r)
            xsphVel += (1.0 / max(rho_j, 1.0)) * (v_j - newVel) * kernelPoly6(r2, h, poly6C);
          }
        }
      }
    }
  }

  // Apply XSPH smoothing
  newVel += params.pbf_xsphC * xsphVel;

  // Write final position and velocity
  positionMass[i] = vec4<f32>(newPos, positionMass[i].w);
  velocityAge[i] = vec4<f32>(newVel, velocityAge[i].w + params.dt);

  // Clear force, update max velocity
  forceVmax[i] = vec4<f32>(0.0, 0.0, 0.0, length(newVel));
}
`;

export const PBF_UPDATE_VELOCITY_XSPH = PBF_UPDATE_VELOCITY_XSPH_SHADER;

// ============================================================================
// SHADER METADATA
// ============================================================================

export const PBF_SHADER_METADATA = {
  name: 'PBF (Position-Based Fluids)',
  passes: [
    { name: 'predictPositions', shader: PBF_PREDICT_POSITIONS },
    { name: 'computeLambda', shader: PBF_COMPUTE_LAMBDA },
    { name: 'computeDeltaPos', shader: PBF_COMPUTE_DELTA_POS },
    { name: 'applyDeltaPos', shader: PBF_APPLY_DELTA_POS },
    { name: 'updateVelocityXSPH', shader: PBF_UPDATE_VELOCITY_XSPH }
  ],
  bindings: PBF_SOLVER_BINDINGS,
  description: 'Position-based incompressibility constraint solver with XSPH viscosity'
};
